import { useLocation } from "react-router";
import { TenantLink } from "~/lib/navigation";
import { TenantBrand, type LogoSizeTier } from "@butter/ui/components/data-display";
import { HamburgerIcon, CloseIcon } from "@butter/ui/icons";

export interface MainHeaderProps {
  /** Active tenant handle — drives which brand mark renders. */
  tenantHandle?: string;
  /** Club name shown beside the brand mark. */
  clubName?: string;
  isAuthenticated: boolean;
  /** Shows "Book a Lesson" — the club has a bookable instructor. @default false */
  showBookLesson?: boolean;
  /** Shows "Lesson Packages" — the club lists a lesson package. @default false */
  showLessonPackages?: boolean;
  /** Member holds a lesson pack at this club — shows "Plans". @default false */
  showPlans?: boolean;
  userFirstName?: string;
  userLastName?: string;
  isMobileMenuOpen: boolean;
  onToggleMobileMenu: () => void;
  onSignIn: () => void;
  onLogout: () => void;
  /** Size tier for the tenant brand mark (shape-aware). Defaults to 'xs' — tuned for the h-16 bar. */
  brandSize?: LogoSizeTier;
}

/**
 * MainHeader — the sticky top header for the member web portal.
 *
 * Presentational: all data and callbacks are passed in by `MainLayout`, so the
 * header can be previewed and its tenant-logo sizing tested in isolation (see
 * MainHeader.stories.tsx). Requires a router context for its `TenantLink`s.
 */
export function MainHeader({
  tenantHandle,
  clubName,
  isAuthenticated,
  showBookLesson = false,
  showLessonPackages = false,
  showPlans = false,
  userFirstName,
  userLastName,
  isMobileMenuOpen,
  onToggleMobileMenu,
  onSignIn,
  onLogout,
  brandSize = "xs",
}: MainHeaderProps) {
  // Clear booking context when navigating home so users start fresh.
  const clearBooking = () => window.dispatchEvent(new Event("butter:clearBooking"));
  const { pathname } = useLocation();
  const isCurrent = (to: string) =>
    to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`);
  const isBookLessonCurrent =
    pathname.startsWith("/lessons/book") || pathname.startsWith("/lessons/confirmed");
  const isLessonPackagesCurrent =
    pathname === "/lessons" || pathname.startsWith("/lessons/purchase");

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white shadow-sm">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <TenantLink
              to="/"
              onClick={clearBooking}
              className="flex items-center gap-2 transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 rounded-sm"
              aria-label="Home"
            >
              <TenantBrand tenantHandle={tenantHandle} size={brandSize} />
              {clubName && (
                <span className="text-sm font-semibold text-gray-900 leading-none">
                  {clubName}
                </span>
              )}
            </TenantLink>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Desktop Navigation Links - Hidden on mobile */}
            <nav
              className="hidden md:flex items-center gap-6 mr-4"
              aria-label="Main navigation"
            >
              <TenantLink
                to="/"
                onClick={clearBooking}
                aria-current={isCurrent("/") ? "page" : undefined}
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 rounded-sm aria-[current=page]:text-theme-800"
                data-testid="header-booking-link">
                Make a Booking
              </TenantLink>
              <TenantLink
                to="/subscriptions"
                aria-current={isCurrent("/subscriptions") ? "page" : undefined}
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 rounded-sm aria-[current=page]:text-theme-800"
                data-testid="header-subscriptions-link"
              >
                Subscriptions
              </TenantLink>
              {showBookLesson && (
                <TenantLink
                  to="/lessons/book"
                  aria-current={isBookLessonCurrent ? "page" : undefined}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 rounded-sm aria-[current=page]:text-theme-800"
                  data-testid="header-book-lesson-link"
                >
                  Book a Lesson
                </TenantLink>
              )}
              {showLessonPackages && (
                <TenantLink
                  to="/lessons"
                  aria-current={isLessonPackagesCurrent ? "page" : undefined}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 rounded-sm aria-[current=page]:text-theme-800"
                  data-testid="header-lessons-link"
                >
                  Lesson Packages
                </TenantLink>
              )}
              {showPlans && (
                <TenantLink
                  to="/plans"
                  aria-current={isCurrent("/plans") ? "page" : undefined}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 rounded-sm aria-[current=page]:text-theme-800"
                  data-testid="header-plans-link"
                >
                  Plans
                </TenantLink>
              )}
            </nav>
            {isAuthenticated ? (
              /* Authenticated: user menu. Desktop only — on mobile, sign-out lives in
                 the slide-out menu beside sign-in, so the bar keeps just the hamburger. */
              <div className="hidden md:flex items-center gap-3">
                <span
                className="text-sm text-gray-700"
                data-testid="header-user-name">
                  {userFirstName} {userLastName}
                </span>
                <button
                  onClick={onLogout}
                  className="inline-flex items-center justify-center rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2"
                  data-testid="header-signout-button"
                >
                  Sign out
                </button>
              </div>
            ) : (
              /* Unauthenticated: sign in. Desktop only, for the same reason. */
              <nav className="hidden md:inline-flex">
                <button
                  onClick={onSignIn}
                  className="inline-flex items-center justify-center rounded-full border border-theme-600 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2"
                  data-testid="header-signin-button"
                >
                  Sign in
                </button>
              </nav>
            )}

            {/* Mobile menu button - Only visible on mobile, transforms hamburger ↔ X */}
            <button
              type="button"
              onClick={onToggleMobileMenu}
              className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-theme-600 transition-colors"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-open-btn"
            >
              {isMobileMenuOpen ? (
                <CloseIcon ariaHidden className="size-6" />
              ) : (
                <HamburgerIcon ariaHidden className="size-6" />
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
